import React, { useCallback, useState } from 'react';

import { TeamUUID } from '../../../../../../teams/teamsTypes';
import {
  ChangeLifestylesFiltersFunc,
  FetchLifestylesFilterFields,
  FetchLifestylesFilters
} from '../../../../../../lifestyles/lifestylesTypes';
import { IsDisabled } from '../../../../../../../types';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import { LifestylesPermissions } from '../../../../../../lifestyles/lifestylesConstants';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockLifestylesFiltersFavoritesFilterProps {
  teamUuid?: TeamUUID;
  lifestylesFilters: FetchLifestylesFilters;
  disabled?: IsDisabled;
  changeLifestylesFilters: ChangeLifestylesFiltersFunc;
}

const favoriteFieldName = FetchLifestylesFilterFields.FAVORITE;
const teamIdFavoriteFieldName = FetchLifestylesFilterFields.TEAM_ID_FAVORITE;
const currentTeamFavoriteFieldName =
  FetchLifestylesFilterFields.CURRENT_TEAM_FAVORITE;

function ThreeDStockLifestylesFiltersFavoritesFilter({
  teamUuid,
  lifestylesFilters,
  disabled,
  changeLifestylesFilters
}: ThreeDStockLifestylesFiltersFavoritesFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const isFavorites = lifestylesFilters?.[favoriteFieldName];
  const handleFavoriteClick = useCallback(() => {
    if (isFavorites) {
      changeLifestylesFilters({}, [favoriteFieldName]);
    } else {
      changeLifestylesFilters({ [favoriteFieldName]: true }, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }
  }, [isFavorites, changeLifestylesFilters]);

  const isTeamFavorites = lifestylesFilters?.[teamIdFavoriteFieldName];
  const isCurrentTeamFavorites =
    lifestylesFilters?.[currentTeamFavoriteFieldName];

  const handleTeamFavoriteClick = useCallback(() => {
    if (isTeamFavorites || isCurrentTeamFavorites) {
      return changeLifestylesFilters({}, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }

    const filterName = teamUuid
      ? teamIdFavoriteFieldName
      : currentTeamFavoriteFieldName;

    const filterValue = teamUuid ? teamUuid : true;

    changeLifestylesFilters({ [filterName]: filterValue }, [favoriteFieldName]);
  }, [
    isTeamFavorites,
    isCurrentTeamFavorites,
    changeLifestylesFilters,
    teamUuid
  ]);

  return (
    <CheckPermissions
      actions={[
        LifestylesPermissions.READ_LIFESTYLES_FAVORITE_CHECKBOX_FILTER,
        LifestylesPermissions.READ_LIFESTYLES_MY_COMPANY_FAVORITE_CHECKBOX_FILTER
      ]}
    >
      <div className="p-4">
        <div className="flex flex-col gap-3">
          <ButtonHelper
            className="flex items-center gap-2 rounded-sm"
            dataGa="favorites"
            onClick={toggleIsOpen}
          >
            <span className="text-sm font-semibold text-left flex-1">
              <Translate id={words.favorites} />
            </span>

            <Icon
              icon={
                isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP
              }
              className="h-5 w-5 text-gray-500"
            />
          </ButtonHelper>

          {isOpen && (
            <div className="flex flex-wrap gap-2">
              <CheckPermissions
                action={
                  LifestylesPermissions.READ_LIFESTYLES_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureIconButtonHelper
                  className={
                    isFavorites
                      ? 'border rounded text-sm flex items-center h-8 box-content border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'border rounded text-sm flex items-center h-8 box-content border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  }
                  icon={IconsEnum.HEART_SOLID}
                  iconClassName="-mr-2 ml-2 h-4 w-4 text-pink-500"
                  i18nText={words.favorites}
                  i18nTextClassName="px-3 whitespace-nowrap"
                  onClick={handleFavoriteClick}
                  dataGa="favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  LifestylesPermissions.READ_LIFESTYLES_MY_COMPANY_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureButtonHelper
                  className={
                    isTeamFavorites || isCurrentTeamFavorites
                      ? 'px-3 border rounded text-sm flex items-center h-8 box-content border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'px-3 border rounded text-sm flex items-center h-8 box-content border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  }
                  i18nText={words.companyFavorites}
                  i18nTextClassName="inline"
                  onClick={handleTeamFavoriteClick}
                  dataGa="team-favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>
            </div>
          )}
        </div>
      </div>
    </CheckPermissions>
  );
}

export default ThreeDStockLifestylesFiltersFavoritesFilter;
